/**
 * Aubergine — Schurke (Rogue). LMAO-Style (BATTLE-14):
 * Persoenlichkeit "lauernder Attentaeter" — schlanker, leicht geduckter Nachtschatten-
 * Korpus mit kraeftigem Auberginen-Glanz (Signature-Gloss-Streak + Specular), der
 * gruene Kelch ist seine KAPUZE (Kelch-Blattspitzen rahmen das Gesicht, weicher
 * Schattenwurf auf die Stirn, Augenpartie bleibt frei), drueber ein muede haengender
 * Stiel als Kapuzen-Zipfel. 2★: Leder-Guertel + Wurfdolch-Scheiden + Kapuzen-Spange.
 * 3★: violett glimmende Schatten-Runen (pulsieren mit castPulse) + Schatten-Aura.
 * Referenz-Qualitaet: kohlrabi.ts. Contract: species/types.ts.
 */
import type { PaintParams, SpeciesPainter } from './types'
import { darken, glow, lighten, rgba, rimLight, specular, volumeGradient } from '../draw3d'

export const paintAubergine: SpeciesPainter = (p: PaintParams) => {
  const { ctx, x, y, r, colors, tier, pose } = p
  const dead = p.emotion === 'dead'
  const bx = (f: number): number => x + f * r
  const by = (f: number): number => y + f * r
  ctx.save()
  ctx.lineWidth = Math.max(2, r * 0.12)
  ctx.strokeStyle = colors.outline
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'

  // --- Stiel (hinter der Kapuze): haengt muede ueber die Dom-Seite — Kapuzen-Zipfel ---
  ctx.save()
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = Math.max(2.5, r * 0.16)
  ctx.beginPath()
  ctx.moveTo(bx(0.06), by(-1.04))
  ctx.quadraticCurveTo(bx(0.42), by(-1.3), bx(0.62), by(-0.86))
  ctx.stroke()
  ctx.strokeStyle = darken(colors.accent, 8)
  ctx.lineWidth = Math.max(1.5, r * 0.1)
  ctx.stroke()
  ctx.strokeStyle = rgba(lighten(colors.accent, 22), dead ? 0.3 : 0.7)
  ctx.lineWidth = Math.max(1, r * 0.035)
  ctx.beginPath()
  ctx.moveTo(bx(0.1), by(-1.08))
  ctx.quadraticCurveTo(bx(0.38), by(-1.26), bx(0.55), by(-0.92))
  ctx.stroke()
  ctx.restore()

  // --- Korpus: schlanke Auberginen-Birne, leicht geduckt (asymmetrischer Lean) ---
  const bodyPath = (): void => {
    ctx.beginPath()
    ctx.moveTo(bx(0.06), by(-0.8)) // Nacken unter der Kapuze
    ctx.bezierCurveTo(bx(-0.34), by(-0.82), bx(-0.56), by(-0.45), bx(-0.6), by(-0.02))
    ctx.bezierCurveTo(bx(-0.64), by(0.42), bx(-0.52), by(0.88), bx(-0.04), by(0.96))
    ctx.bezierCurveTo(bx(0.46), by(0.9), bx(0.67), by(0.5), bx(0.63), by(0.06))
    ctx.bezierCurveTo(bx(0.6), by(-0.42), bx(0.4), by(-0.8), bx(0.06), by(-0.8))
    ctx.closePath()
  }
  bodyPath()
  ctx.fillStyle = volumeGradient(ctx, x, y + r * 0.05, r, colors.body, 20, 26)
  ctx.fill()
  ctx.stroke()

  // Schattenflanke rechts (Nachtschatten-Woelbung, nutzt bodyDark)
  ctx.save()
  bodyPath()
  ctx.clip()
  ctx.strokeStyle = rgba(darken(colors.bodyDark, 10), 0.45)
  ctx.lineWidth = r * 0.18
  ctx.beginPath()
  ctx.moveTo(bx(0.54), by(-0.35))
  ctx.quadraticCurveTo(bx(0.7), by(0.2), bx(0.44), by(0.8))
  ctx.stroke()
  // Po-Falte unten (Auberginen-Charakteristik, dezent)
  ctx.strokeStyle = rgba(darken(colors.body, 30), 0.5)
  ctx.lineWidth = Math.max(1, r * 0.05)
  ctx.beginPath()
  ctx.moveTo(bx(-0.14), by(0.88))
  ctx.quadraticCurveTo(bx(0.0), by(0.98), bx(0.14), by(0.88))
  ctx.stroke()
  ctx.restore()

  // --- Auberginen-GLANZ: langer Gloss-Streak + Glanzpunkt (Signature) ---
  if (!dead) {
    ctx.save()
    bodyPath()
    ctx.clip()
    ctx.strokeStyle = rgba(lighten(colors.body, 48), 0.6)
    ctx.lineWidth = r * 0.1
    ctx.beginPath()
    ctx.moveTo(bx(-0.44), by(-0.28))
    ctx.quadraticCurveTo(bx(-0.58), by(0.12), bx(-0.4), by(0.58))
    ctx.stroke()
    // kurzer Parallel-Reflex (nasser Lack-Look)
    ctx.strokeStyle = rgba(lighten(colors.body, 40), 0.3)
    ctx.lineWidth = r * 0.045
    ctx.beginPath()
    ctx.moveTo(bx(-0.28), by(0.42))
    ctx.quadraticCurveTo(bx(-0.36), by(0.62), bx(-0.22), by(0.76))
    ctx.stroke()
    ctx.restore()
    specular(ctx, bx(-0.47), by(-0.05), r * 0.15, 0.65)
    rimLight(ctx, x, y + r * 0.08, r * 0.62, r * 0.95, rgba(lighten(colors.body, 55), 0.4), Math.max(1.5, r * 0.06))
  }

  // --- 2★: Leder-Guertel mit Schnalle + Wurfdolch-Scheiden (Ruestungs-Detail) ---
  if (tier >= 2 && !dead) {
    const beltY = by(0.56)
    const leather = darken(colors.bodyDark, 14)
    const metal = lighten(colors.outline, 48)
    ctx.save()
    const bg = ctx.createLinearGradient(x, beltY - r * 0.13, x, beltY + r * 0.13)
    bg.addColorStop(0, lighten(leather, 10))
    bg.addColorStop(0.5, leather)
    bg.addColorStop(1, darken(leather, 16))
    ctx.fillStyle = bg
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1.5, r * 0.06)
    ctx.beginPath()
    ctx.ellipse(x, beltY, r * 0.62, r * 0.13, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
    // Naht oben
    ctx.strokeStyle = rgba(lighten(leather, 32), 0.7)
    ctx.lineWidth = Math.max(1, r * 0.025)
    ctx.beginPath()
    ctx.ellipse(x, beltY - r * 0.04, r * 0.56, r * 0.09, 0, Math.PI * 1.1, Math.PI * 1.9)
    ctx.stroke()
    // Schnalle (Raute — Assassinen-Emblem)
    ctx.translate(x, beltY)
    ctx.rotate(Math.PI / 4)
    ctx.fillStyle = metal
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1, r * 0.045)
    const s = r * 0.1
    ctx.fillRect(-s, -s, s * 2, s * 2)
    ctx.strokeRect(-s, -s, s * 2, s * 2)
    ctx.fillStyle = darken(metal, 30)
    ctx.fillRect(-s * 0.35, -s * 0.35, s * 0.7, s * 0.7)
    ctx.restore()
    // zwei kleine Dolch-Scheiden, haengen nach unten (Dual-Wield-Echo, docs/12 §3)
    for (const side of [-1, 1] as const) {
      ctx.save()
      ctx.translate(bx(side * 0.42), by(0.74))
      ctx.rotate(side * 0.16)
      ctx.fillStyle = darken(leather, 8)
      ctx.strokeStyle = colors.outline
      ctx.lineWidth = Math.max(1, r * 0.045)
      ctx.beginPath()
      ctx.moveTo(-r * 0.055, -r * 0.09)
      ctx.lineTo(r * 0.055, -r * 0.09)
      ctx.lineTo(0, r * 0.21)
      ctx.closePath()
      ctx.fill()
      ctx.stroke()
      ctx.fillStyle = darken(metal, 12)
      ctx.fillRect(-r * 0.065, -r * 0.14, r * 0.13, r * 0.045)
      ctx.restore()
    }
  }

  // --- 3★: violett glimmende Schatten-Runen + Schatten-Aura (castPulse) ---
  if (tier >= 3 && !dead) {
    const runeGlow = lighten(colors.body, 46)
    glow(ctx, x, y + r * 0.25, r * 1.15, runeGlow, 0.12 + pose.castPulse * 0.28)
    ctx.save()
    ctx.strokeStyle = rgba(lighten(colors.body, 52), 0.65 + pose.castPulse * 0.35)
    ctx.lineWidth = Math.max(1.5, r * 0.055)
    ctx.shadowColor = runeGlow
    ctx.shadowBlur = r * (0.22 + pose.castPulse * 0.14)
    // drei Assassinen-Glyphen (deterministisch, meiden Gesicht + Guertel)
    for (const [gx, gy, flip] of [[-0.42, 0.16, 1], [0.44, 0.3, -1], [0.0, 0.8, 1]] as const) {
      const rx = bx(gx)
      const ry = by(gy)
      ctx.beginPath()
      ctx.moveTo(rx - flip * r * 0.08, ry - r * 0.11)
      ctx.lineTo(rx + flip * r * 0.06, ry - r * 0.02)
      ctx.lineTo(rx - flip * r * 0.05, ry + r * 0.04)
      ctx.lineTo(rx + flip * r * 0.08, ry + r * 0.12)
      ctx.stroke()
    }
    ctx.restore()
  }

  // --- Kapuzen-Schattenwurf auf die Stirn (Tiefe; endet UEBER der Augenpartie) ---
  ctx.save()
  const sg = ctx.createLinearGradient(x, by(-0.64), x, by(-0.12))
  sg.addColorStop(0, rgba(colors.outline, 0.38))
  sg.addColorStop(1, rgba(colors.outline, 0))
  ctx.fillStyle = sg
  ctx.beginPath()
  ctx.moveTo(bx(0.86), by(-0.12))
  ctx.lineTo(bx(0.6), by(0.1))
  ctx.lineTo(bx(0.44), by(-0.48))
  ctx.quadraticCurveTo(bx(0.2), by(-0.68), bx(0.0), by(-0.56))
  ctx.quadraticCurveTo(bx(-0.2), by(-0.68), bx(-0.44), by(-0.48))
  ctx.lineTo(bx(-0.6), by(0.1))
  ctx.lineTo(bx(-0.86), by(-0.12))
  ctx.lineTo(bx(-0.56), by(0.24))
  ctx.quadraticCurveTo(bx(0.0), by(-0.3), bx(0.56), by(0.24))
  ctx.closePath()
  ctx.fill()
  ctx.restore()

  // --- Kelch-KAPUZE: flacher Dom (in die Schultern geduckt), Wangen-Klappen
  //     haengen seitlich herab und rahmen das Gesicht, Stirn-Bogen laesst Augen frei ---
  const hoodPath = (): void => {
    ctx.beginPath()
    ctx.moveTo(bx(-0.9), by(-0.14)) // linker Saum aussen
    // Haube (leicht nach rechts geneigt — lauernde Asymmetrie), Spitze oben
    ctx.bezierCurveTo(bx(-1.06), by(-0.62), bx(-0.62), by(-1.16), bx(0.08), by(-1.18))
    ctx.bezierCurveTo(bx(0.7), by(-1.12), bx(1.0), by(-0.56), bx(0.9), by(-0.14))
    // rechte Wangen-Klappe: Kelch-Blattspitze haengt bis auf Augenhoehe
    ctx.lineTo(bx(0.62), by(0.08))
    ctx.lineTo(bx(0.44), by(-0.5))
    // Stirn-Bogen mit kleiner Mittelspitze (Augenbrauen bleiben frei)
    ctx.quadraticCurveTo(bx(0.2), by(-0.72), bx(0.0), by(-0.6))
    ctx.quadraticCurveTo(bx(-0.2), by(-0.72), bx(-0.44), by(-0.5))
    // linke Wangen-Klappe
    ctx.lineTo(bx(-0.62), by(0.08))
    ctx.closePath()
  }
  const hg = ctx.createLinearGradient(bx(-0.7), by(-1.25), bx(0.5), by(-0.1))
  hg.addColorStop(0, lighten(colors.accent, 24))
  hg.addColorStop(0.55, colors.accent)
  hg.addColorStop(1, darken(colors.accent, 20))
  hoodPath()
  ctx.fillStyle = hg
  ctx.fill()
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = Math.max(2, r * 0.12)
  ctx.stroke()

  // Stoff-Falten der Kapuze (Tiefe) + Blattrippen in den Wangen-Klappen
  ctx.save()
  ctx.strokeStyle = rgba(darken(colors.accent, 26), 0.55)
  ctx.lineWidth = Math.max(1, r * 0.05)
  ctx.beginPath()
  ctx.moveTo(bx(-0.08), by(-1.04))
  ctx.quadraticCurveTo(bx(-0.26), by(-0.84), bx(-0.36), by(-0.58))
  ctx.moveTo(bx(0.22), by(-1.0))
  ctx.quadraticCurveTo(bx(0.34), by(-0.8), bx(0.4), by(-0.56))
  ctx.moveTo(bx(-0.74), by(-0.28))
  ctx.quadraticCurveTo(bx(-0.7), by(-0.1), bx(-0.64), by(0.0))
  ctx.moveTo(bx(0.74), by(-0.28))
  ctx.quadraticCurveTo(bx(0.7), by(-0.1), bx(0.64), by(0.0))
  ctx.stroke()
  ctx.restore()

  // Kapuzen-Licht: Rim oben-links + kleiner Glanz
  if (!dead) {
    ctx.save()
    ctx.strokeStyle = rgba(lighten(colors.accent, 50), 0.65)
    ctx.lineWidth = Math.max(1.5, r * 0.06)
    ctx.beginPath()
    ctx.moveTo(bx(-0.84), by(-0.42))
    ctx.quadraticCurveTo(bx(-0.62), by(-1.0), bx(-0.12), by(-1.08))
    ctx.stroke()
    ctx.restore()
    specular(ctx, bx(-0.34), by(-0.88), r * 0.12, 0.35)
  }

  // 2★: Metall-Spange an der Kapuzen-Mittelspitze
  if (tier >= 2 && !dead) {
    ctx.save()
    ctx.fillStyle = lighten(colors.outline, 48)
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1, r * 0.04)
    ctx.beginPath()
    ctx.arc(bx(0.0), by(-0.63), r * 0.055, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
    ctx.fillStyle = rgba(lighten(colors.outline, 70), 0.8)
    ctx.beginPath()
    ctx.arc(bx(-0.015), by(-0.645), r * 0.02, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }

  ctx.restore()
}
